import React, { Suspense, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { CommentWithReplyField } from '@contently/toolkit';
import { useFetcher } from 'rest-hooks';
import useAvatarBadge from '../../hooks/useAvatarBadge';
import authorOwnerCheck from '../../utils/authorOwnerCheck';
import useUserContext from '../../hooks/useUserContext';
import CommentResource from '../../resources/CommentResource';
import ReplyChainContainer from './ReplyChainContainer';
import { getReplyCommentParams } from './utils';
import LoadingSpinner from '../../../utils/shared/LoadingSpinner';
import {
  updateResolvedAnnotation,
  updateAnnotationListCacheBust
} from '../../store/textEditorReducer';

export default function CommentWrapper({ storyId, handleCancel, active, comment, users }) {
  const {
    id: commentId,
    authorable,
    authorIsEmployee,
    authorIsManagingEditor,
    resolved,
    highlightedText
  } = comment;
  const [resolveClicked, toggleResolveClicked] = useState(false);
  const { permissions } = useUserContext();
  const editComment = useFetcher(CommentResource.updateShape());
  const createComment = useFetcher(CommentResource.createShape());
  const dispatch = useDispatch();

  const handleEdit = (id, data) => {
    editComment({ id }, { body: data.edit });
  };

  const handleResolve = (e, id) => {
    if (e) e.preventDefault();
    if (comment.uuid) dispatch(updateResolvedAnnotation(comment.uuid));
    editComment({ id }, { resolved: 1 });
    dispatch(updateAnnotationListCacheBust(true));
    toggleResolveClicked(true);
  };

  const handleReply = (data, id) => {
    createComment({}, getReplyCommentParams(data, storyId, id), [
      [
        CommentResource.listShape(),
        { parent_id: id },
        (newCommentId, commentIds) => [...(commentIds || []), newCommentId]
      ]
    ]);
  };

  const [avatarTooltip, avatarBadge, avatarBadgeColor] = useAvatarBadge(
    authorable.fullName,
    authorIsEmployee,
    authorIsManagingEditor
  );

  return (
    <CommentWithReplyField
      {...comment}
      fullName={authorable.fullName}
      avatar={authorable.avatar}
      authorIsOwner={authorOwnerCheck(authorable.id, permissions.id)}
      authorTooltip={avatarTooltip}
      authorBadge={avatarBadge}
      authorBadgeColor={avatarBadgeColor}
      resolved={resolved}
      highlightedText={highlightedText}
      onResolve={e => handleResolve(e, comment.id)}
      onReplyCancel={e => handleCancel(e)}
      onEditCancel={e => handleCancel(e)}
      onReplySubmit={e => handleReply(e, comment.id)}
      onEditSubmit={data => {
        handleEdit(commentId, data);
      }}
      width="100%"
      showReplyForm={active && !(resolved || resolveClicked)}
      borderBottom={false}
      borderSides={false}
      users={users}
    >
      <Suspense fallback={<LoadingSpinner />}>
        <ReplyChainContainer commentId={comment.id} showAllComments={active} sidebar />
      </Suspense>
    </CommentWithReplyField>
  );
}

CommentWrapper.propTypes = {
  storyId: PropTypes.number.isRequired,
  handleCancel: PropTypes.func.isRequired,
  active: PropTypes.bool.isRequired,
  comment: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    authorable: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      fullName: PropTypes.string,
      avatar: PropTypes.string
    }).isRequired,
    resolved: PropTypes.number.isRequired,
    authorIsEmployee: PropTypes.bool,
    authorIsManagingEditor: PropTypes.bool,
    uuid: PropTypes.string,
    highlightedText: PropTypes.string
  }).isRequired,
  users: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      display: PropTypes.string.isRequired,
      avatar: PropTypes.string.isRequired
    })
  ).isRequired
};



// WEBPACK FOOTER //
// ./app/javascript/client/components/textEditor/CommentWrapper.jsx